/* eslint-disable no-console */
/* eslint-disable max-lines */
import React, { useRef, useEffect } from 'react';
import HandleButton from '@/components/HandleButton';
import { usePersistFn } from 'ahooks';
import { Modal, Toast } from 'antd-mobile';
import { StepModelsState, useIntl, useSelector, useDispatch, FormModelsState } from 'umi';
import { CloseOutlined } from '@ant-design/icons';
import { MoBaseContainer as BaseContainer } from '@gov-mo/components';
import HeaderProgress from '@/components/HeaderProgress';
import { stepInfo, StepType } from '@/utils/ServiceConf';
import { Page } from '@gov-mo/mpaas-js-bridge';
import GuideButton from '@/components/GuideButton';
import StepsPopover from './components/StepsPopover';
import styles from './ServiceStep.less';
import StepRender from './components/StepRender';
import useHandleStep from './components/StepRender/HandleStep';
import { isPaySuccess } from './services/serviceStepApi';

function ServiceStep() {
  const dispatch = useDispatch();
  // 獲取國際化配置對象
  const intl = useIntl();

  // 从models里取状态
  const { step, maxStep } = useSelector((x: { stepModels: StepModelsState }) => x.stepModels);
  const { payData } = useSelector((x: { formModels: FormModelsState }) => x.formModels);

  // 當前步驟
  const currentStep = stepInfo[step - 1];

  // 用于步骤切换时回到顶部操作
  const scrollRef = useRef<HTMLDivElement>(null);

  // 点击右上角"x"的时候，提示框的控制
  const goBackModal = useRef<{ close: () => void } | null>(null);

  // 更新/初始化models
  const updataModels = usePersistFn((payload) => {
    dispatch({
      type: 'stepModels/update',
      payload,
    });
  });

  useEffect(() => {
    // 记录当前到过的最大步骤数，用于步骤间跳转
    if (maxStep < step) {
      updataModels({ maxStep: step });
    }
  }, [step]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [step]);

  /**
   * 上一步按鈕點擊
   */
  const handlePrevStep = usePersistFn(async () => {
    updataModels({ step: step - 1 });
  });

  /**
   * 退出时弹出的对话框方法
   */
  const handleGoBackModal = usePersistFn(() => {
    if (goBackModal.current) return;
    goBackModal.current = Modal.alert(
      intl.formatMessage({ id: '温馨提示' }),
      intl.formatMessage({ id: '确认放弃当前操作，并退出' }),
      [
        {
          text: intl.formatMessage({ id: '取消' }),
          onPress: () => {
            goBackModal.current?.close();
            goBackModal.current = null;
          },
        },
        {
          text: intl.formatMessage({ id: '确认' }),
          onPress: () => {
            Page.close();
          },
        },
      ],
    );
  });

  /**
   * 下一步按鈕點擊
   */
  const handleNextStep = useHandleStep(currentStep);

  /**
   * 返回
   */
  const handleBackOrStepChange = usePersistFn(() => {
    // 第一步為返回并清空models
    if (step === 1) {
      handleGoBackModal();
    } else if (currentStep.stepType === StepType.payment) {
      // 事项账单页面，返回操作，添加申请支付状态查询（逻辑同“付款”按钮），若是已支付，不允许返回，直接进入下一步
      Toast.loading(intl.formatMessage({ id: '請稍候' }), 0);
      isPaySuccess(payData.billId as string).then((res) => {
        Toast.hide();
        if (res.data) {
          handleNextStep();
        } else {
          handlePrevStep();
        }
      });
    } else {
      // 其余为返回上一步
      handlePrevStep();
    }
  });

  /**
   * 底部按鈕文字
   */
  const renderButtonText = usePersistFn(() => {
    let text = '';
    switch (currentStep.stepType) {
      case StepType.dataCheck:
        text = intl.formatMessage({ id: '確認' });
        break;
      case StepType.payment:
        text = intl.formatMessage({ id: '立即付款' });
        break;
      default:
        text = intl.formatMessage({ id: '下一步' });
    }
    return text;
  });

  return (
    <div className={styles.baseRoot}>
      <BaseContainer
        type="custom"
        bodyContentClass={styles.bodyContent}
        titleContent=""
        onLeftClick={handleBackOrStepChange as (param?: unknown) => void}
        // header右上角申請步驟條按鈕&關閉按鈕
        rightContent={(
          <div className={styles.headerRightContent}>
            <StepsPopover stepList={stepInfo.map((i) => i.stepName)} current={step} />
            <div onClick={handleGoBackModal} className={styles.iconArea}>
              <CloseOutlined
                className={styles.icon}
                role="button"
                aria-label={intl.formatMessage({ id: '关闭' })}
              />
            </div>
          </div>
        )}
      >
        <div className={styles.root}>
          <div className={styles.main} ref={scrollRef}>
            {/* header下的服務標題 */}
            <HeaderProgress
              title={intl.formatMessage({ id: 'Demo事项' })}
              progressArr={stepInfo.map((i) => i.stepName)}
              current={step}
            />
            {/* 主要內容區域 */}
            <StepRender
              currentStep={currentStep}
              stepInfoList={stepInfo}
              handleNext={handleNextStep}
              mobile={false}
            />
          </div>

          {/* 右側的浮動圖標按鈕 */}
          <GuideButton />

          {/* 底部的上一步下一步按鈕 */}
          <HandleButton
            rightButton={{
              text: renderButtonText(),
              onClick: handleNextStep,
            }}
          />
        </div>
      </BaseContainer>
    </div>
  );
}

export default ServiceStep;
